import {
  ProviderChrtHighRiskTaskArguments,
  ProviderManualTaskArguments,
  ProviderSelfHarmTaskArguments,
  ProviderTask,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
  ProviderTslTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { baseApi, RTKQueryTag } from '../../../data/baseApi'

type GetProviderTasksParams = {
  providerId: string
}

type PatchProviderTaskPayload<T extends Record<string, unknown>> = {
  id: string
  kind: ProviderTaskType
  data: Partial<ProviderTask<T>>
}

type CreateProviderManualTaskPayload = {
  providerId: string
  data: ProviderManualTaskArguments
}

const taskTag = (kind: ProviderTaskType) => ({ type: RTKQueryTag.PROVIDER_TASK, id: kind })

const providerTasksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProviderSelfHarmTasks: builder.query<ProviderTask<ProviderSelfHarmTaskArguments>[], GetProviderTasksParams>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/v1/provider/task',
        params: { provider_id: providerId, type: ProviderTaskType.SELF_HARM },
      }),
      providesTags: [taskTag(ProviderTaskType.SELF_HARM)],
    }),
    getProviderTriageRiskTasks: builder.query<ProviderTask<ProviderTriageRiskTaskArguments>[], GetProviderTasksParams>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/v1/provider/task',
        params: { provider_id: providerId, type: ProviderTaskType.TRIAGE_RISK },
      }),
      providesTags: [taskTag(ProviderTaskType.TRIAGE_RISK)],
    }),
    getProviderChrtHighRiskTasks: builder.query<
      ProviderTask<ProviderChrtHighRiskTaskArguments>[],
      GetProviderTasksParams
    >({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/v1/provider/task',
        params: { provider_id: providerId, type: ProviderTaskType.CHRT_HIGH_RISK },
      }),
      providesTags: [taskTag(ProviderTaskType.CHRT_HIGH_RISK)],
    }),
    getProviderManualTasks: builder.query<ProviderTask<ProviderManualTaskArguments>[], GetProviderTasksParams>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/v1/provider/task',
        params: { provider_id: providerId, type: ProviderTaskType.MANUAL, status: 'requires_acknowledgment' },
      }),
      providesTags: [taskTag(ProviderTaskType.MANUAL)],
    }),
    createProviderManualTask: builder.mutation<
      ProviderTask<ProviderManualTaskArguments>,
      CreateProviderManualTaskPayload
    >({
      query: ({ providerId, data }) => ({
        method: 'post',
        url: '/v1/provider/task/manual',
        data: {
          provider_id: providerId,
          status: 'requires_acknowledgment',
          type: ProviderTaskType.MANUAL,
          arguments: data,
        },
      }),
      invalidatesTags: [taskTag(ProviderTaskType.MANUAL)],
    }),
    getProviderTslTasks: builder.query<ProviderTask<ProviderTslTaskArguments>[], GetProviderTasksParams>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/v1/provider/task',
        params: {
          provider_id: providerId,
          type: ProviderTaskType.TREATMENT_SUMMARY_LETTER,
          status: 'requires_acknowledgment',
        },
      }),
      transformResponse: (tasks: ProviderTask<ProviderTslTaskArguments>[]) =>
        (tasks ?? []).filter((task) => task.resource_id),
      providesTags: [taskTag(ProviderTaskType.TREATMENT_SUMMARY_LETTER)],
    }),
    patchProviderTask: builder.mutation<
      ProviderTask<Record<string, unknown>>,
      PatchProviderTaskPayload<Record<string, unknown>>
    >({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `/v1/provider/task/${id}`,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [taskTag(arg.kind)],
    }),
  }),
})

export const {
  useGetProviderSelfHarmTasksQuery,
  useLazyGetProviderSelfHarmTasksQuery,
  useGetProviderTriageRiskTasksQuery,
  useGetProviderChrtHighRiskTasksQuery,
  useGetProviderManualTasksQuery,
  useGetProviderTslTasksQuery,
  useCreateProviderManualTaskMutation,
  usePatchProviderTaskMutation,
} = providerTasksApi
